import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ApiService } from '../../../core/api-service';
import { Store } from '@ngrx/store';
import { insightsComponentDestroyed, LocationComparisonActions, LocationComparisonCountActions, MapLocationsActions, PostsPerWeekdayActions, PostTimesActions, RatingActions, RespondedCommentedActions, ResponseTimesActions, ResponseTimesOverviewActions, ReviewNumbersActions, syncInsightsFilters, syncLocationComparisonTableSettings, TopLocationsActions, updateFiltersForSuccessfulInsightsCall } from './insights-actions';
import { selectAccountID } from '../../../store/global/core-selectors';
import { debounce, EMPTY, mergeMap, of, timer } from 'rxjs';
import { selectInsightsFilters, selectLocationComparisonTableSettings } from './insights-selectors';
import { InsightsMethod } from '../../../models/review-insights-models/insights-method';
import { catchError, filter, map, retry, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '../../../models/api-response';
import { RatingResponse } from '../../../models/review-insights-models/rating-response';
import { ReviewNumbersResponse } from '../../../models/review-insights-models/review-numbers-response';
import { LocationComparisonOverviewResponseEntry } from '../../../models/review-insights-models/location-comparison-overview-response-entry';
import { LocationComparisonResponseEntry } from '../../../models/review-insights-models/location-comparison-response-entry';
import { RespondedCommentedResponse } from '../../../models/review-insights-models/responded-commented-response';
import { ResponseTimesResponse } from '../../../models/review-insights-models/response-times-response';
import { ChartDatapointSeries } from '../../../models/review-insights-models/chart-datapoint-series';
import { ChartDatapoint } from '../../../models/review-insights-models/chart-datapoint';
import { MapLocationInterface } from '../../../models/review-insights-models/map-location';
import { selectCurrentRoute } from '../../../store/global/router-selectors';
import { getRefreshActionsForInsightsPage } from '../utils';


@Injectable()
export class InsightsEffects {
  constructor(private actions$: Actions,
              private apiService: ApiService,
              private store: Store) {
  }

  getRating$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                RatingActions.getRating,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.RATINGS)
                    .pipe(
                        mergeMap((res: ApiResponse<RatingResponse>) => [RatingActions.getRatingSuccess({
                          rating: res?.result,
                        }), updateFiltersForSuccessfulInsightsCall({
                          filters,
                          insightsMethod: InsightsMethod.RATINGS,
                        })]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(RatingActions.getRatingFail());
                        }),
                    ),
            ),
        )
        ;
  });

  getReviewNumbers$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                ReviewNumbersActions.getReviewNumbers,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.NUMBER_OF_REVIEWS)
                    .pipe(
                        mergeMap((res: ApiResponse<ReviewNumbersResponse>) =>
                            [ReviewNumbersActions.getReviewNumbersSuccess({ reviewNumbers: res?.result }),
                              updateFiltersForSuccessfulInsightsCall({
                                filters,
                                insightsMethod: InsightsMethod.NUMBER_OF_REVIEWS,
                              })]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(ReviewNumbersActions.getReviewNumbersFail());
                        }),
                    ),
            ),
        );
  });

  getTopLocations$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                TopLocationsActions.getTopLocations,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.TOP_LOCATIONS)
                    .pipe(
                        mergeMap((res: ApiResponse<LocationComparisonOverviewResponseEntry[]>) =>
                            [TopLocationsActions.getTopLocationsSuccess({ topLocations: res?.result }),
                              updateFiltersForSuccessfulInsightsCall({
                                filters,
                                insightsMethod: InsightsMethod.TOP_LOCATIONS,
                              })]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(TopLocationsActions.getTopLocationsFail());
                        }),
                    ),
            ),
        );
  });

  getLocationComparisonList$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                LocationComparisonActions.getLocationComparisonList,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
              this.store.select(selectLocationComparisonTableSettings),
            ]),
            filter(([action, accountId, filters, tableSettings]) => !!accountId),
            switchMap(([action, accountId, filters, tableSettings]) => {
                  const _filters = { ...filters, ...tableSettings };

                  return action.type === insightsComponentDestroyed.type ?
                      EMPTY :
                      this.apiService.getInsights(accountId, _filters, InsightsMethod.LOCATION_COMPARISON_LIST)
                          .pipe(
                              mergeMap((res: ApiResponse<LocationComparisonResponseEntry[]>) =>
                                  [LocationComparisonActions.getLocationComparisonListSuccess({
                                    locationComparisonList: res?.result,
                                  }), updateFiltersForSuccessfulInsightsCall({
                                    filters: _filters,
                                    insightsMethod: InsightsMethod.LOCATION_COMPARISON_LIST,
                                  })]),
                              retry(1),
                              catchError((err: HttpErrorResponse) => {
                                console.error(err);
                                return of(LocationComparisonActions.getLocationComparisonListFail());
                              }),
                          );
                },
            ),
        );
  });

  getLocationComparisonCount$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                LocationComparisonCountActions.getLocationComparisonCount,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.LOCATION_COMPARISON_COUNT)
                    .pipe(
                        mergeMap((res: ApiResponse<number>) => [
                          LocationComparisonCountActions.getLocationComparisonCountSuccess(
                              { locationComparisonCount: res?.result }),
                          updateFiltersForSuccessfulInsightsCall({
                            filters,
                            insightsMethod: InsightsMethod.LOCATION_COMPARISON_COUNT,
                          }),
                        ]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(LocationComparisonCountActions.getLocationComparisonCountFail());
                        }),
                    ),
            ),
        );
  });

  getRespondedCommented$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                RespondedCommentedActions.getRespondedCommented,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.RESPONDED_COMMENTED)
                    .pipe(
                        mergeMap((res: ApiResponse<RespondedCommentedResponse>) =>
                            [RespondedCommentedActions.getRespondedCommentedSuccess({ respondedCommented: res?.result }),
                              updateFiltersForSuccessfulInsightsCall({
                                filters,
                                insightsMethod: InsightsMethod.RESPONDED_COMMENTED,
                              })]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(RespondedCommentedActions.getRespondedCommentedFail());
                        }),
                    ),
            ),
        );
  });

  getResponseTimes$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                ResponseTimesActions.getResponseTimes,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.RESPONSE_TIMES)
                    .pipe(
                        mergeMap((res: ApiResponse<ResponseTimesResponse>) =>
                            [ResponseTimesActions.getResponseTimesSuccess({ responseTimes: res?.result }),
                              updateFiltersForSuccessfulInsightsCall({
                                filters,
                                insightsMethod: InsightsMethod.RESPONSE_TIMES,
                              })]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(ResponseTimesActions.getResponseTimesFail());
                        }),
                    ),
            ),
        );
  });

  getResponseTimesOverview$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                ResponseTimesOverviewActions.getResponseTimesOverview,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.RESPONSE_TIMES_OVERVIEW)
                    .pipe(
                        mergeMap((res: ApiResponse<number>) => [ResponseTimesOverviewActions.getResponseTimesOverviewSuccess({
                          responseTimesOverview: res?.result,
                        }), updateFiltersForSuccessfulInsightsCall({
                          filters,
                          insightsMethod: InsightsMethod.RESPONSE_TIMES_OVERVIEW,
                        })]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(ResponseTimesOverviewActions.getResponseTimesOverviewFail());
                        }),
                    ),
            ),
        );
  });

  getPostTimes$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                PostTimesActions.getPostTimes,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.POST_TIMES)
                    .pipe(
                        mergeMap((res: ApiResponse<ChartDatapointSeries[]>) => [PostTimesActions.getPostTimesSuccess({
                          postTimes: res?.result,
                        }), updateFiltersForSuccessfulInsightsCall({
                          filters,
                          insightsMethod: InsightsMethod.POST_TIMES,
                        })]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(PostTimesActions.getPostTimesFail());
                        }),
                    ),
            ),
        );
  });

  getPostsPerWeekday$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                PostsPerWeekdayActions.getPostsPerWeekday,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, filters, InsightsMethod.POSTS_PER_WEEKDAY)
                    .pipe(
                        mergeMap((res: ApiResponse<ChartDatapoint[]>) => [PostsPerWeekdayActions.getPostsPerWeekdaySuccess({
                          postsPerWeekday: res?.result,
                        }), updateFiltersForSuccessfulInsightsCall({
                          filters,
                          insightsMethod: InsightsMethod.POSTS_PER_WEEKDAY,
                        })]),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(PostsPerWeekdayActions.getPostsPerWeekdayFail());
                        }),
                    ),
            ),
        );
  });

  getMapLocations$ = createEffect(() => {
    return this.actions$
        .pipe(
            ofType(
                MapLocationsActions.getMapLocations,
                insightsComponentDestroyed,
            ),
            concatLatestFrom(() => [
              this.store.select(selectAccountID),
              this.store.select(selectInsightsFilters),
            ]),
            filter(([action, accountId, filters]) => !!accountId),
            switchMap(([action, accountId, filters]) => action.type === insightsComponentDestroyed.type ?
                EMPTY :
                this.apiService.getInsights(accountId, action.ignoreFilters ? null : filters, InsightsMethod.MAP_LOCATIONS)
                    .pipe(map((res: ApiResponse<MapLocationInterface[]>) => MapLocationsActions.getMapLocationsSuccess({
                          mapLocations: res.result,
                        })),
                        retry(1),
                        catchError((err: HttpErrorResponse) => {
                          console.error(err);
                          return of(MapLocationsActions.getMapLocationsFail());
                        }),
                    ),
            ),
        );
  });


  debouncedHandleSyncFiltersRequest$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(syncInsightsFilters, syncLocationComparisonTableSettings),
        // optional debounce
        debounce((action) => action.type === syncInsightsFilters.type ?
            timer(500) :
            of(null)),
        filter((action) => !action.preventUpdate), // allow only API call requests
        concatLatestFrom(() => this.store.select(selectCurrentRoute)),
        mergeMap(([action, currentRoute]) => {
          return getRefreshActionsForInsightsPage(currentRoute,
              action.type === syncLocationComparisonTableSettings.type);
        }),
    );
  });


}
